import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';
import {
  applyTheme, isPreference, readStoredPreference, resolveTheme, storePreference, systemPrefersDark,
} from '../theme/themeStore';

const ThemeContext = createContext({
  preference: 'lab_light', theme: 'lab_light', setPreference: () => {},
});

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.user_id;
  const [preference, setPreferenceState] = useState(readStoredPreference);
  const [theme, setTheme] = useState(() => resolveTheme(preference, systemPrefersDark()));

  useEffect(() => {
    setTheme(applyTheme(preference));
    if (preference !== 'system' || !window.matchMedia) return undefined;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const follow = () => setTheme(applyTheme('system'));
    if (query.addEventListener) query.addEventListener('change', follow);
    return () => { if (query.removeEventListener) query.removeEventListener('change', follow); };
  }, [preference]);

  // On sign-in, a choice saved on the server (another device) wins over this
  // browser's. A server that has none -- or an older backend without the route --
  // leaves the local choice alone.
  useEffect(() => {
    if (!userId) return undefined;
    let live = true;
    api.get('/users/me/preferences')
      .then(({ data }) => {
        if (live && isPreference(data?.ui_theme)) {
          setPreferenceState(data.ui_theme);
          storePreference(data.ui_theme);
        }
      })
      .catch(() => {});
    return () => { live = false; };
  }, [userId]);

  const setPreference = useCallback((next) => {
    if (!isPreference(next)) return;
    setPreferenceState(next);
    storePreference(next);
    if (userId) api.put('/users/me/preferences', { ui_theme: next }).catch(() => {});
  }, [userId]);

  return (
    <ThemeContext.Provider value={{ preference, theme, setPreference }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
